import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../utils/api';
import MeasurementForm from '../components/MeasurementForm';
import MeasurementTable from '../components/MeasurementTable';

export default function Measurements() {
  const { user }           = useAuth();
  const { isDark, theme }  = useTheme();
  const navigate           = useNavigate();

  const [measurements, setMeasurements] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [profileOk,    setProfileOk]    = useState(true);
  const [gender,       setGender]       = useState(null);
  const [isNew,        setIsNew]        = useState(false);
  const [editItem,     setEditItem]     = useState(null); // для редактирования
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const acc  = theme.accent;
  const glow = theme.glow;
  const txt  = isDark ? '#F2EEF9' : '#2D1B35';
  const mut  = isDark ? 'rgba(200,185,220,.55)' : 'rgba(80,60,100,.5)';
  const card = isDark ? '#1A1526' : '#FFFFFF';
  const bdr  = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.06)';

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/measurements/with-dynamics'),
      api.get('/users/profile'),
    ]).then(([mRes, pRes]) => {
      setMeasurements(mRes.data.measurements || []);
      const u = pRes.data.user;
      setProfileOk(!!(u?.gender && u?.height_cm && u?.birth_date && u?.goal && u?.activity_level));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Сохранить новый замер
  const handleCreate = async (values) => {
    setSaving(true); setError('');
    try {
      await api.post('/measurements', values);
      load(); setIsNew(false);
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Обновить существующий замер
  const handleUpdate = async (values) => {
    setSaving(true); setError('');
    try {
      await api.put(`/measurements/${editItem.id}`, values);
      load(); setEditItem(null);
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  };

  // Удалить замер
  const handleDelete = async (id) => {
    if (!confirm('Удалить этот замер?')) return;
    try {
      await api.delete(`/measurements/${id}`);
      load();
    } catch(err) { alert(err.message); }
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'4rem' }}>
      <div style={{ width:32, height:32, border:`3px solid ${acc}30`, borderTopColor:acc,
                    borderRadius:'50%', animation:'spin .8s linear infinite' }}/>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );

  // Профиль не заполнен
  if (!profileOk) return (
    <div style={{ maxWidth:480, margin:'2rem auto', padding:'2rem',
                  background:card, border:`1.5px solid ${acc}30`, borderRadius:18,
                  textAlign:'center' }}>
      <p style={{ fontSize:'2rem', margin:'0 0 .75rem' }}>⚠️</p>
      <h3 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:700, fontSize:'1.15rem', margin:'0 0 .5rem' }}>
        Сначала заполни профиль
      </h3>
      <p style={{ color:mut, fontSize:'.875rem', margin:'0 0 1.25rem', lineHeight:1.55 }}>
        Нужны: пол, дата рождения, рост, цель и уровень активности
      </p>
      <Link to="/profile" style={{ display:'inline-block', padding:'.7rem 1.5rem',
                                    borderRadius:10, background:acc, color:'#fff',
                                    textDecoration:'none', fontWeight:700,
                                    boxShadow:`0 4px 16px ${glow}` }}>
        Заполнить профиль →
      </Link>
    </div>
  );

  // Форма нового замера
  if (isNew) return (
    <div style={{ maxWidth:600, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button onClick={() => setIsNew(false)} style={{
          background:'none', border:`1px solid ${bdr}`, borderRadius:8,
          padding:'6px 12px', cursor:'pointer', color:mut, fontSize:'.82rem',
        }}>← Назад</button>
        <h2 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:700, fontSize:'1.2rem', margin:0 }}>
          Новый замер
        </h2>
      </div>
      {error && <div style={{ background:'rgba(239,68,68,.12)', border:'1px solid rgba(239,68,68,.3)',
                               borderRadius:10, padding:'10px 14px', marginBottom:14, color:'#EF4444', fontSize:'.85rem' }}>
        ⚠️ {error}
      </div>}
      <div style={{ background:card, border:`1px solid ${bdr}`, borderRadius:16, padding:'20px' }}>
        <MeasurementForm onSubmit={handleCreate} loading={saving} onCancel={() => setIsNew(false)} gender={gender}/>
      </div>
    </div>
  );

  // Форма редактирования
  if (editItem) return (
    <div style={{ maxWidth:600, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button onClick={() => setEditItem(null)} style={{
          background:'none', border:`1px solid ${bdr}`, borderRadius:8,
          padding:'6px 12px', cursor:'pointer', color:mut, fontSize:'.82rem',
        }}>← Назад</button>
        <h2 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:700, fontSize:'1.2rem', margin:0 }}>
          Редактировать замер
        </h2>
        <button onClick={() => handleDelete(editItem.id)} style={{
          marginLeft:'auto', background:'rgba(239,68,68,.1)', border:'1px solid rgba(239,68,68,.25)',
          borderRadius:8, padding:'6px 12px', cursor:'pointer', color:'#EF4444', fontSize:'.78rem',
        }}>Удалить</button>
      </div>
      {error && <div style={{ background:'rgba(239,68,68,.12)', border:'1px solid rgba(239,68,68,.3)',
                               borderRadius:10, padding:'10px 14px', marginBottom:14, color:'#EF4444', fontSize:'.85rem' }}>
        ⚠️ {error}
      </div>}
      <div style={{ background:card, border:`1px solid ${bdr}`, borderRadius:16, padding:'20px' }}>
        <MeasurementForm initial={editItem} onSubmit={handleUpdate} loading={saving} onCancel={() => setEditItem(null)} gender={gender}/>
      </div>
    </div>
  );

  // Главный вид — таблица
  return (
    <div style={{ maxWidth:800, margin:'0 auto', fontFamily:'Inter,system-ui,sans-serif' }}>
      <style>{'@keyframes _in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}'}</style>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20,
                    animation:'_in .35s cubic-bezier(.16,1,.3,1) both' }}>
        <div>
          <h1 style={{ color:txt, fontFamily:'Nunito,system-ui', fontWeight:800, fontSize:'1.6rem',
                       margin:'0 0 3px', letterSpacing:-.5 }}>Замеры</h1>
          <p style={{ color:mut, fontSize:'.82rem', margin:0 }}>
            {measurements.length > 0
              ? `${measurements.length} замер${measurements.length===1?'':'а' } · нажми дату чтобы отредактировать`
              : 'Ещё нет замеров'}
          </p>
        </div>
        <button onClick={() => setIsNew(true)} style={{
          padding:'.65rem 1.25rem', borderRadius:12, background:acc, border:'none',
          color:'#fff', fontSize:'.875rem', fontWeight:700, cursor:'pointer',
          boxShadow:`0 4px 16px ${glow}`, transition:'all .2s',
        }}>
          + Новый замер
        </button>
      </div>

      {/* Таблица замеров */}
      {measurements.length === 0 ? (
        <div style={{ background:card, border:`1px solid ${bdr}`, borderRadius:16,
                      padding:'3rem', textAlign:'center',
                      animation:'_in .4s cubic-bezier(.16,1,.3,1) .05s both' }}>
          <p style={{ fontSize:'2.5rem', margin:'0 0 12px' }}>📏</p>
          <p style={{ color:txt, fontWeight:600, fontSize:'1rem', margin:'0 0 6px' }}>Нет замеров</p>
          <p style={{ color:mut, fontSize:'.85rem', margin:'0 0 20px' }}>
            Добавь первый замер утром, натощак, после туалета
          </p>
          <button onClick={() => setIsNew(true)} style={{
            padding:'.65rem 1.4rem', borderRadius:10, background:acc, border:'none',
            color:'#fff', fontWeight:700, cursor:'pointer', boxShadow:`0 4px 14px ${glow}`,
          }}>
            Добавить первый замер
          </button>
        </div>
      ) : (
        <div style={{ animation:'_in .4s cubic-bezier(.16,1,.3,1) .05s both' }}>
          <MeasurementTable
            measurements={measurements}
            onEdit={(m) => setEditItem(m)}
          />
          <p style={{ color:mut, fontSize:'.72rem', textAlign:'center', marginTop:10 }}>
            ✏️ Нажми «ред.» под датой чтобы исправить замер · Зелёный = прогресс, красный = регресс
          </p>
        </div>
      )}
    </div>
  );
}
